import React from 'react'
import Styles from './JupyterTerminal.module.scss'
import { useState, useEffect } from 'react';
import { encoder } from 'utils/helpers/decoder';


// Module imports.
import MonacoEditor from '@monaco-editor/react';
import FeatherIcon from 'feather-icons-react';
import DoneAllIcon from '@mui/icons-material/Check';
import axios from 'axios';
import { toast } from 'react-toastify';
import { LightTooltip } from 'components/global/NodeLayout/NodeTopostman';
import { useTranslation } from 'react-i18next';


export function JupyterTerminal(props) {
    const [output, setOutput] = useState({})
    const [codeExecuted, setCodeExecuted] = useState(false);
    const [runStatus, setRunStatus] = useState(false)
    const [isRunBtnDisabled, setIsRunBtnDisabled] = useState(true);
    const { t } = useTranslation();
    const [dimensions, setDimensions] = useState({
        width: window.innerWidth * 0.52,
        height: window.innerHeight * 0.25,
    });

    useEffect(() => {
        const handleResize = () => {
            setDimensions({
                width: window.innerWidth * 0.52,
                height: window.innerHeight * 0.25,
            });
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    function handleError() {
        toast.dark(t('Components.NodeLayout.JupyterTerminal.toast.error.warn'), {
            type: 'error',
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });
    }

    function handleEmptyResponse() {
        toast.dark(t('Components.NodeLayout.JupyterTerminal.toast.timeout.warn'), {
            type: 'error',
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });
    }

    const executeHandler = async () => {
        try {
            setRunStatus(true)
            props.resetActivityTimer();
            const res = await axios.post("api/jupyter", { code: props.terminal.command, userPublicIp: encoder(props.userPublicIp) })
            if (!(res.data.text === '' && res.data.error === '' && res.data.data === '')) {
                setCodeExecuted(true)
                props.setCodeSubmit(true)
            } else {
                handleEmptyResponse()
            }
            setOutput(res.data)
        } catch (error) {
            setCodeExecuted(false)
            handleError()
        } finally {
            setRunStatus(false)
        }
    }

    const handleInputChange = (e) => {
        props.handleCellValueChange(props.ind, "command", e);
        setIsRunBtnDisabled(false);
        props.resetActivityTimer();
    };

    return (
        <>
            <div data-testid="jupyterTerminal-editorContainer" className={Styles.editorContainer}>
                <div data-testid="jupyterTerminal-editorContainer-editorHeader" id="editorHeader" className={Styles.editorHeader}>
                    <div data-testid="jupyterTerminal-editorContainer-count" style={{ display: 'flex', alignItems: 'center' }}>
                        <p
                            style={{
                                fontSize: "large",
                                fontFamily: "monospace",
                                fontWeight: "bold",
                                background: '#f0f2f4',
                                padding: '0px 10px',
                                borderRadius: '15px'
                            }}
                        >
                            [{props.ind + 1 < 10 ? `0${props.ind + 1}` : props.ind + 1}]
                        </p>
                        {
                            codeExecuted === true &&
                            <span style={{ color: '#4ade80', marginLeft: '20px', fontSize: '16px' }}>
                                <DoneAllIcon
                                    fontSize='small'
                                    sx={{
                                        color: "#4ade80",

                                    }}
                                />
                                <span style={{ marginLeft: '5px' }}>
                                    Executed
                                </span>
                            </span>
                        }
                    </div>
                    <span data-testid="jupyterTerminal-editorContainer-runButtonContainer" className={Styles.runButtonContainer}>
                        <LightTooltip
                            title={t('Components.NodeLayout.JupyterTerminal.tooltip.run')}
                            arrow
                            classes={{
                                tooltip: {
                                    backgroundColor: '#fff',
                                    color: 'rgba(0, 0, 0, 0.87)',
                                    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.15)',
                                    fontSize: 11,
                                },
                            }}
                        >
                            <span>
                                <button data-testid="jupyterTerminal-editorContainer-runButton" className={Styles.runButton} onClick={executeHandler}
                                    disabled={isRunBtnDisabled || props?.userInput[0]?.command?.replace(/\s/g, '') === ''}
                                >
                                    {runStatus ?
                                        <FeatherIcon color='black' icon='circle' />
                                        :
                                        <FeatherIcon color='black' icon='play' />
                                    }
                                </button>
                            </span>
                        </LightTooltip>
                    </span>
                </div>
                <div data-testid="jupyterTerminal-editorContainer-monacoEditor" style={{ display: "flex" }}>
                    <MonacoEditor
                        width={dimensions?.width}
                        height={dimensions?.height}
                        theme="vs-light"
                        defaultLanguage='python'
                        value={props.savedSteps ? props.terminal.command : ''}
                        onChange={handleInputChange}
                        options={{
                            minimap: {
                                enabled: false,
                            },
                            scrollbar: {
                                vertical: 'hidden',
                                alwaysConsumeMouseWheel: false,
                            },
                            readOnly: props.terminalLength - 1 !== props.ind,
                            fontSize: 16,
                            wordWrap: 'on',
                            glyphMargin: false,
                            folding: false,
                            lineDecorationsWidth: 10,
                            lineNumbersMinChars: 2,
                            glyphMarginDecorationLaneCount: 0,
                            overviewRulerLanes: 0,
                            lineNumbers: false,
                        }} />
                </div>
                <div data-testid="jupyterTerminal-editorContainer-result" style={{
                    width: '100%', padding: '0.5em',
                    borderTop:
                        output?.text || output?.error || output?.data
                            ? "1px solid #bbb7b7"
                            : "none",
                }}>
                    {output.text && (<div data-testid="jupyterTerminal-editorContainer-result-output" style={{ marginTop: '0.5em', width: `${dimensions?.width}` }}>
                        <h3 style={{
                            fontSize: '16px',
                            fontWeight: 'bold'
                        }}>{t('Components.NodeLayout.JupyterTerminal.output.title')}</h3>
                        <span style={{ display: "flex", alignItems: "center", padding: '0.5em' }}>
                            <pre className={Styles.jupyterTerminalOutput}>
                                {output.text}
                            </pre>
                        </span>

                    </div>
                    )} {output.error && (<div data-testid="jupyterTerminal-editorContainer-result-error" style={{ marginTop: '0.5em', width: `${dimensions?.width}` }}>
                        <h3 style={{
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: 'red'
                        }}>{t('Components.NodeLayout.JupyterTerminal.error.title')}</h3>
                        <span style={{ display: "flex", alignItems: "center", padding: '0.5em' }}>
                            <pre className={Styles.jupyterTerminalOutput}>
                                {output.error}
                            </pre>
                        </span>

                    </div>
                    )} {output.data && (<div data-testid="jupyterTerminal-editorContainer-result-plot" style={{ marginTop: '0.5em', width: `${dimensions?.width}` }}>
                        <h3 style={{
                            fontSize: '16px',
                            fontWeight: 'bold'
                        }}>{t('Components.NodeLayout.JupyterTerminal.data.title')}</h3>
                        <span style={{ display: "flex", alignItems: "center", padding: '0.5em' }}>
                            <img
                                className={Styles.jupyterTerminalOutput}
                                src={`data:image/png;base64,${output.data}`}
                                alt='graphs'
                            />
                        </span>
                    </div>
                    )}
                </div >
            </div>
        </>
    )
}
